import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Globe, Shield, TrendingUp } from 'lucide-react'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { ChatFab } from '@/components/portal/ChatFab'
import { LanguageSelect } from '@/components/portal/LanguageSelect'
import { REGULATORY_LOGOS } from '@/constants/regulatory-logos'

interface AuthShellProps {
  children: ReactNode
}

export function AuthShell({ children }: AuthShellProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set())
  const isWelcome = location.pathname === '/'
  const isLogin = location.pathname === '/login' || location.pathname === '/sign-in'
  const isRegister = location.pathname === '/sign-up'

  function handleLogoError(src: string) {
    setFailedLogos((prev) => new Set(prev).add(src))
  }

  return (
    <div className="prod-auth">
      <div className="prod-auth__grid">
        <div className="prod-auth__brand">
          <div className="prod-auth__brand-content">
            <img src="/regol/esafx-lockup.svg" alt="EsaFX" className="prod-auth__brand-logo" />
            <p className="prod-auth__brand-tag">{t('auth.brandTag')}</p>
            <h2 className="prod-auth__brand-title">{t('auth.brandTitle')}</h2>
            <p className="prod-auth__brand-sub">{t('auth.brandSubtitle')}</p>
            <div className="prod-auth__benefits">
              <span className="prod-auth__benefit">
                <span className="prod-auth__benefit-icon" aria-hidden="true">
                  <Shield className="size-4" />
                </span>
                {t('auth.benefitSecure')}
              </span>
              <span className="prod-auth__benefit">
                <span className="prod-auth__benefit-icon" aria-hidden="true">
                  <TrendingUp className="size-4" />
                </span>
                {t('auth.benefitReliable')}
              </span>
              <span className="prod-auth__benefit">
                <span className="prod-auth__benefit-icon" aria-hidden="true">
                  <Globe className="size-4" />
                </span>
                {t('auth.benefitGlobal')}
              </span>
            </div>
          </div>
          <div className="prod-auth__brand-footer">
            <p className="prod-auth__brand-tag prod-auth__brand-tag--spaced">{t('auth.licenses')}</p>
            <div className="prod-auth__regulatory" aria-label={t('regol.regulatoryAria')}>
              {REGULATORY_LOGOS.map((logo) =>
                failedLogos.has(logo.src) ? (
                  <span key={logo.src} className="prod-auth__regulatory-fallback-chip">
                    {logo.label}
                  </span>
                ) : (
                  <img
                    key={logo.src}
                    src={logo.src}
                    alt={logo.alt}
                    className={'sizeClass' in logo ? logo.sizeClass : undefined}
                    onError={() => handleLogoError(logo.src)}
                  />
                ),
              )}
            </div>
          </div>
        </div>

        <div className="prod-auth__panel">
          <div className="prod-auth__topbar">
            <Link to="/" className="prod-shell__topbar-logo lg:hidden">
              <img
                src="/regol/esafx-lockup.svg"
                alt="EsaFX"
                className="prod-shell__logo prod-shell__logo--lockup"
              />
            </Link>
            <div className="prod-auth__topbar-right">
              <ThemeToggle variant="regol" />
              <LanguageSelect variant="panel" />
              <div className={`prod-auth__tabs ${isWelcome ? 'prod-auth__tabs--welcome-hidden' : ''}`}>
                <Link
                  to="/login"
                  className={`prod-auth__tab ${isLogin ? 'prod-auth__tab--active-login' : ''}${isLogin ? ' prod-auth__tab--hidden' : ''}`}
                >
                  {t('auth.signIn')}
                </Link>
                <Link
                  to="/sign-up"
                  className={`prod-auth__tab ${isRegister ? 'prod-auth__tab--active-register' : ''}${isRegister ? ' prod-auth__tab--hidden' : ''}`}
                >
                  {t('auth.signUp')}
                </Link>
              </div>
            </div>
          </div>
          <div className="prod-auth__form-wrap">
            <div className="prod-auth__form-card">{children}</div>
          </div>
        </div>
      </div>
      <ChatFab />
    </div>
  )
}
